/**
 * Blog post registry — the single source of truth for /blog.
 *
 * One entry per post. This array drives the sitemap entries in
 * `src/config/seo.ts`, and `blogPosts.test.ts` checks it against what is
 * actually on disk in `public/blog/`, so the registry and the published files
 * cannot drift apart.
 *
 * ── Posts are static HTML, not routes ──────────────────────────────────────
 * A post is a self-contained file at `public/blog/<file>`, copied verbatim into
 * the build by Vite and synced to the stage bucket by `scripts/sync-stage.sh`.
 * Nothing here renders a post; React never sees one.
 *
 * ── The `.html` extension is load-bearing. Do not "clean up" these URLs. ────
 * The CloudFront Function at `infra/lib/site-stack.ts:86` rewrites any request
 * with no dot and no trailing slash to `/index.html`, and `site-stack.ts:162`
 * maps every S3 403/404 to `/index.html` at HTTP 200. So:
 *
 *   /blog/<slug>        → rewritten       → the SPA shell, HTTP 200
 *   /blog/<slug>/       → S3 miss → 404   → the SPA shell, HTTP 200
 *   /blog/<slug>.html   → passes through  → the post
 *
 * Dropping the extension does not 404. It silently serves the homepage with a
 * success status, which no error report will ever surface. `blogPosts.test.ts`
 * asserts the extension for exactly this reason.
 *
 * ── Adding a post ─────────────────────────────────────────────────────────
 * See `docs/blog.md`. In short: drop the file in `public/blog/`, add an entry
 * here, run the tests.
 */

import type { AgentSlug } from '../agents/registry.ts';

export interface BlogPost {
  /**
   * Filename under `public/blog/`, date-prefixed and `.html`-suffixed:
   * `YYYY-MM-DD-<kebab-slug>.html`. The date prefix must equal `date`.
   * This is permanent once published — it is the shared URL.
   */
  readonly file: string;
  /** Headline. Should match the post's own <h1> and <title>. */
  readonly title: string;
  /** Standfirst: one or two sentences, used on the index and in the sitemap description. */
  readonly dek: string;
  /** Publication date, ISO `YYYY-MM-DD`. Never changes after publication. */
  readonly date: string;
  /** Section label shown above the headline, e.g. 'Leadership'. */
  readonly kicker: string;
  /**
   * The agent that actually drafted the post — a real slug from
   * `src/agents/registry.ts`, so a byline cannot name an agent that does not
   * exist. This is an attribution claim, not decoration: the bylined agent
   * must be the one that did the work.
   */
  readonly author: AgentSlug;
  /** The person who set the question and edited, credited alongside the agent. */
  readonly withHuman: string;
  /**
   * A third-party AI model that assisted after the authoring agent drafted the
   * piece — editing, restructuring, and formatting it for the web.
   *
   * Optional because most posts will not involve one; absent simply means no
   * such credit line renders. When present it MUST name the actual model used,
   * and it must appear in the post's attribution block — `blogPosts.test.ts`
   * and `tests/e2e/blog.spec.ts` both assert that it does. Like `author`, this
   * is an attribution claim rather than decoration.
   */
  readonly editor?: string;
  /**
   * Whether the post is publicly discoverable.
   *
   * `false` means published-but-unlisted: reachable at its URL, absent from the
   * sitemap, linked from nowhere, and carrying `<meta name="robots"
   * content="noindex">` in the file itself. `blogPosts.test.ts` asserts that
   * this flag and that meta tag agree, so the two halves of "unlisted" cannot
   * come apart.
   *
   * This is a FIELD rather than a comment on purpose. `src/config/seo.ts:85`
   * records twelve days of widget pages missing from the sitemap because the
   * release condition was written as a comment nobody was watching.
   */
  readonly listed: boolean;
}

/** Newest first. This order is the display order. */
export const BLOG_POSTS: readonly BlogPost[] = [
  {
    file: '2026-09-30-the-checklist-method-for-llms.html',
    title:
      'The Checklist Method: How Explicit, Checkable Lists Improve LLM Prompts, Evaluations, and Workflows',
    dek:
      'Checklists improve LLM performance most when used as explicit, checkable criteria—not as formatting tricks.',
    date: '2026-09-30',
    kicker: 'AI Engineering',
    author: 'vera',
    withHuman: 'Andrew Chud',
    editor: 'Claude Opus 5.5',
    listed: true,
  },
  {
    file: '2026-09-30-the-checklist-method.html',
    title:
      'The Checklist Method: Why Specific Prompts Outperform General Instructions',
    dek:
      'Vague guidance fails. The gain comes from specificity—which the checklist method forces—but format matters too. No single format wins everywhere.',
    date: '2026-09-30',
    kicker: 'Prompt Engineering',
    author: 'vera',
    withHuman: 'Andrew Chud',
    editor: 'Claude Opus 5.5',
    /** Unlisted 2026-09-30 at Andrew's direction: superseded by
     *  2026-09-30-the-checklist-method-for-llms, which covers the same evidence
     *  and more, and the two read as one post twice on /blog. Kept published so
     *  the URL — briefly listed in prod — still shows the article instead of
     *  falling through to the SPA catch-all. */
    listed: false,
  },
  {
    file: '2026-09-07-threat-rigidity-in-tech-leadership.html',
    title:
      'The Threat-Rigidity Trap: How Leaders Respond to AI Disruption in Software Engineering',
    dek:
      'When leaders meet a change they cannot navigate, they tighten their grip — and stop hearing the people who could help them. AI transformation is reproducing a pattern organizational psychology named forty-five years ago, and the cycle feeds itself.',
    date: '2026-09-07',
    kicker: 'Leadership',
    author: 'vera',
    withHuman: 'Andrew Chud',
    editor: 'Claude Opus 5',
    listed: true,
  },
];

/** Site-relative URL for a post. The `.html` is required — see the header note. */
export function blogPostPath(post: BlogPost): string {
  return `/blog/${post.file}`;
}

/** Posts that are publicly discoverable, newest first. */
export const LISTED_BLOG_POSTS: readonly BlogPost[] = BLOG_POSTS.filter((p) => p.listed);
